<template>
  <div :class="{ 'z-50': isDropdownOpen }" ref="dropdownTarget">
    <div
      class="flex items-center justify-between h-10 p-2 bg-white rounded-md outline outline-gray-300 outline-1"
      @click="disable ? '' : toggleDropdown()">
      <div class="flex gap-1 p-2">
        <div v-if="!selectedItems?.length || tagsOutside" class="text-sm text-gray-500">
          {{ placeHolderText }}
        </div>
        <template v-if="!tagsOutside">
          <div
            v-for="item in selectedItemNames"
            :key="item"
            class="rounded-full align-middle bg-gray-50 py-0.5 px-1 border border-theme-500 text-sm text-theme-500 transition-all shadow-sm text-center">
            {{ item }}
          </div>
        </template>
      </div>
      <Icon
        icon="carbon:chevron-down"
        class="w-4 h-4 transition duration-200 ease-in-out text-theme-600"
        :class="{ 'rotate-180': isDropdownOpen }" />
    </div>

    <div class="w-full mt-1 overflow-visible transition-all transform divide-y">
      <Transition
        enter-active-class="duration-300 ease-out"
        enter-from-class="scale-95 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-active-class="duration-200 ease-in"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-95 opacity-0">
        <div v-if="isDropdownOpen" class="absolute w-full p-2 bg-white shadow-xl">
          <div v-if="listItems?.length">
            <ul
              class="py-2 overflow-y-auto text-sm text-gray-800 max-h-36 scroll-py-2"
              id="options"
              role="listbox">
              <li class="flex gap-2 px-4 py-2 cursor-default select-none" @click="allClicked">
                <BaseCheckbox :model-value="areAllSelected" />
                All
              </li>
              <li
                v-for="item in listItems"
                :key="item.value"
                @click="handleSelection(item)"
                class="flex gap-2 px-4 py-2 cursor-default select-none"
                id="option-5"
                role="option"
                tabindex="-1">
                <BaseCheckbox :model-value="isSelected(item)" />
                {{ item.name }}
              </li>
            </ul>
          </div>
          <p v-else class="p-4 text-sm text-gray-500">
            {{ noResultsText }}
          </p>
        </div>
      </Transition>
    </div>
    <div class="flex flex-wrap gap-1.5 my-1.5" v-if="tagsOutside">
      <div
        v-for="(item, index) in visibleTags"
        :key="index"
        class="flex items-center p-1 text-xs font-medium text-center text-gray-500 align-middle transition-all bg-gray-200 border rounded-md shadow-sm w-fit">
        {{ item.name }}

        <IconXmark
          class="w-3.5 h-3.5 ml-auto cursor-pointer"
          @click="handleSelection(item)"></IconXmark>
      </div>
      <button
        v-if="selectedItems?.length > maxVisibleTags"
        type="button"
        class="mx-1 text-xs hover:text-theme-700"
        @click="toggleVisibleTags">
        {{
          showMore
            ? $t('common.actions.showLess')
            : `${$t('common.actions.showMore')} (${remainingTags})`
        }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';
import type { SelectDropdownItem } from '@/types/Base';
import BaseCheckbox from './BaseCheckbox.vue';
import { Icon } from '@iconify/vue';
import IconXmark from '@/assets/icons/IconXmark.vue';

const selectedItems = defineModel<SelectDropdownItem[]>({ default: [] });
const isDropdownOpen = ref(false);

const dropdownTarget = ref(null);
onClickOutside(dropdownTarget, () => {
  isDropdownOpen.value = false;
});

type Props = {
  listItems: SelectDropdownItem[] | undefined;
  noResultsText?: string;
  count?: number;
  placeHolderText?: string;
  disable?: boolean;
  tagsOutside?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  noResultsText: 'No Results',
  placeHolderText: 'Select Items',
});

const areAllSelected = computed(() => {
  return selectedItems.value?.length == props.listItems?.length;
});

function isSelected(itemSelected: SelectDropdownItem) {
  return selectedItems.value?.map((item) => item.value).includes(itemSelected.value);
}

function allClicked() {
  if (areAllSelected.value) {
    selectedItems.value = [];
  } else {
    selectedItems.value = props.listItems ? JSON.parse(JSON.stringify(props.listItems)) : [];
  }
}

function handleSelection(itemSelected: any) {
  if (!selectedItems.value) return;
  let index = selectedItems.value.findIndex((item) => {
    return item.value === itemSelected.value;
  });
  index === -1 ? selectedItems.value.push(itemSelected) : selectedItems.value.splice(index, 1);
}

const selectedItemNames = computed(() => {
  if (!props.listItems) return [];
  let names = props.listItems
    .filter((item) => {
      return selectedItems.value?.map((i) => i.value).includes(item.value);
    })
    .map((i) => i.name);

  if (names.length > 2) {
    names = [...names.slice(0, 2), '+' + (names.length - 2)];
  }
  return names;
});

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

const visibleTagsNum = ref(20);

const maxVisibleTags = ref(20);

const showMore = ref(false);

const visibleTags = computed(() => {
  if (!selectedItems.value) return [];
  return selectedItems.value.slice(0, visibleTagsNum.value);
});

const remainingTags = computed(() => {
  if (!selectedItems.value || selectedItems.value.length <= visibleTagsNum.value) return 0;
  return selectedItems.value.length - visibleTagsNum.value;
});

function toggleVisibleTags() {
  if (showMore.value) {
    showMore.value = false;
    visibleTagsNum.value = 20;
  } else {
    showMore.value = true;
    visibleTagsNum.value = selectedItems.value?.length || 0;
  }
}
</script>
